import { useEffect, useRef } from 'react';

/**
 * StarField — ported from anna-prime with two tuning changes for converze partners:
 *   1. Sage accent uses converze's --accent rgb (113,180,112) instead of anna's (109,183,132).
 *   2. Defaults to sparse + slow for partner workloads (calmer surface).
 *
 * Performance behaviour (kept verbatim from anna):
 *   - Halves particle count on mobile
 *   - Throttles to ~30fps on mobile
 *   - Pauses when tab is hidden
 *   - Pauses when iOS keyboard is open (visualViewport shrinks >150px)
 */

const IS_MOBILE = typeof window !== 'undefined' && window.innerWidth < 768;
const MOBILE_FRAME_INTERVAL = 1000 / 30;

let mobileKeyboardOpen = false;
if (IS_MOBILE && typeof window !== 'undefined') {
  const vv = window.visualViewport;
  if (vv) {
    const checkKeyboard = () => {
      mobileKeyboardOpen = window.innerHeight - vv.height > 150;
    };
    vv.addEventListener('resize', checkKeyboard);
  }
}

export type StarsDensity = 'sparse' | 'normal' | 'dense';
export type StarsSpeed   = 'slow' | 'normal' | 'fast';
export type StarsAccent  = 'neutral' | 'sage';
export type StarsSize    = 'tiny' | 'normal' | 'large';

export interface StarFieldProps {
  density?: StarsDensity;
  speed?:   StarsSpeed;
  accent?:  StarsAccent;
  size?:    StarsSize;
}

const DENSITY_COUNT: Record<StarsDensity, number> = { sparse: 90, normal: 180, dense: 320 };
const SPEED_MULT:   Record<StarsSpeed,   number> = { slow: 0.45, normal: 1.0, fast: 2.2 };

const ACCENT_COLOR: Record<StarsAccent, [number, number, number]> = {
  neutral: [200, 200, 200],
  sage:    [113, 180, 112],
};

const SIZE_RADIUS: Record<StarsSize, { base: number; accent: number }> = {
  tiny:   { base: 0.35, accent: 0.55 },
  normal: { base: 0.80, accent: 1.10 },
  large:  { base: 1.40, accent: 1.80 },
};

export function StarField({
  density = 'sparse',
  speed   = 'slow',
  accent  = 'sage',
  size    = 'normal',
}: StarFieldProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    interface Particle {
      x: number; y: number;
      r: number; a: number; da: number;
      dx: number; dy: number;
      isAccent: boolean;
    }

    interface Meteor {
      x: number; y: number;
      vx: number; vy: number;
      size: number; length: number;
      opacity: number; life: number; maxLife: number;
    }

    let W = 0, H = 0;
    let particles: Particle[] = [];
    let meteors: Meteor[] = [];
    let nextMeteorIn = 0;
    let animId = 0;
    let isPageVisible = true;
    let isDrawing = false;
    let cursorX = -9999, cursorY = -9999;
    let glowPhase = 0;
    let lastDrawTime = 0;

    const count  = IS_MOBILE ? Math.floor(DENSITY_COUNT[density] / 2) : DENSITY_COUNT[density];
    const sMult  = SPEED_MULT[speed];
    const [aR, aG, aB] = ACCENT_COLOR[accent];
    const { base: rBase, accent: rAccent } = SIZE_RADIUS[size];

    function resize() {
      W = canvas!.width  = canvas!.offsetWidth;
      H = canvas!.height = canvas!.offsetHeight;
    }

    function randParticle(): Particle {
      const isAccent = Math.random() < 0.02;
      return {
        x: Math.random() * W,
        y: Math.random() * H,
        r: isAccent
          ? Math.random() * rAccent * 0.6 + rAccent * 0.7
          : Math.random() * rBase  * 1.2 + rBase  * 0.4,
        a:  Math.random() * 0.18 + 0.04,
        da: (Math.random() - 0.5) * 0.0018,
        dx: (Math.random() - 0.5) * 0.09 * sMult,
        dy: (Math.random() - 0.5) * 0.09 * sMult,
        isAccent,
      };
    }

    function initParticles() {
      particles = Array.from({ length: count }, () => randParticle());
    }

    function spawnMeteor() {
      const fromRight = Math.random() > 0.5;
      const x = fromRight
        ? W * (0.4 + Math.random() * 0.6)
        : W * (Math.random() * 0.6);
      const y = -20;
      const angleDeg = 30 + Math.random() * 25;
      const angleRad = (angleDeg * Math.PI) / 180;
      const speed = Math.random() * 4 + 5;
      const vx = fromRight ? -Math.cos(angleRad) * speed : Math.cos(angleRad) * speed;
      const vy = Math.sin(angleRad) * speed;
      meteors.push({
        x, y,
        vx,
        vy,
        size: 1 + Math.random() * 1.2,
        length: 80 + Math.random() * 100,
        opacity: 0,
        life: 0,
        maxLife: 55 + Math.floor(Math.random() * 25),
      });
    }

    function draw(timestamp = 0) {
      if (!isPageVisible) { isDrawing = false; return; }
      if (IS_MOBILE && mobileKeyboardOpen) { animId = requestAnimationFrame(draw); return; }

      if (IS_MOBILE) {
        const elapsed = timestamp - lastDrawTime;
        if (elapsed < MOBILE_FRAME_INTERVAL) { animId = requestAnimationFrame(draw); return; }
        lastDrawTime = timestamp;
      }

      ctx!.clearRect(0, 0, W, H);

      glowPhase += 0.008;
      const glowAlpha = 0.028 + Math.sin(glowPhase) * 0.012;
      const cx = W * 0.5, cy = H * 0.48;
      const glowR = Math.min(W, H) * 0.38;
      const glow = ctx!.createRadialGradient(cx, cy, 0, cx, cy, glowR);
      glow.addColorStop(0,   `rgba(${aR},${aG},${aB},${glowAlpha})`);
      glow.addColorStop(0.5, `rgba(${aR},${aG},${aB},${glowAlpha * 0.3})`);
      glow.addColorStop(1,   `rgba(${aR},${aG},${aB},0)`);
      ctx!.fillStyle = glow;
      ctx!.fillRect(0, 0, W, H);

      particles.forEach(p => {
        p.a += p.da;
        if (p.a <= 0.02 || p.a >= 0.28) p.da *= -1;

        const dx = cursorX - p.x, dy = cursorY - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 120 && dist > 0) {
          const strength = (1 - dist / 120) * 0.012;
          p.dx += (dx / dist) * strength;
          p.dy += (dy / dist) * strength;
        }

        p.dx *= 0.992; p.dy *= 0.992;
        p.x  += p.dx;  p.y  += p.dy;

        if (p.x < -2)    p.x = W + 2;
        if (p.x > W + 2) p.x = -2;
        if (p.y < -2)    p.y = H + 2;
        if (p.y > H + 2) p.y = -2;

        const colour = p.isAccent
          ? `rgba(${aR},${aG},${aB},${p.a * 1.4})`
          : `rgba(240,238,232,${p.a})`;

        ctx!.beginPath();
        ctx!.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx!.fillStyle = colour;
        ctx!.fill();
      });

      // meteors
      if (!IS_MOBILE) {
        nextMeteorIn--;
        if (nextMeteorIn <= 0) {
          spawnMeteor();
          nextMeteorIn = 180 + Math.floor(Math.random() * 300); // ~3–8s at 60fps
        }

        meteors = meteors.filter(m => m.life < m.maxLife);
        for (const m of meteors) {
          m.life++;
          const t = m.life / m.maxLife;
          m.opacity = t < 0.15 ? t / 0.15 : t > 0.75 ? 1 - (t - 0.75) / 0.25 : 1;
          m.x += m.vx; m.y += m.vy;

          const tailX = m.x - (m.vx / Math.hypot(m.vx, m.vy)) * m.length;
          const tailY = m.y - (m.vy / Math.hypot(m.vx, m.vy)) * m.length;

          const grad = ctx!.createLinearGradient(tailX, tailY, m.x, m.y);
          grad.addColorStop(0, `rgba(255,255,255,0)`);
          grad.addColorStop(1, `rgba(255,255,255,${m.opacity * 0.85})`);
          ctx!.beginPath();
          ctx!.moveTo(tailX, tailY);
          ctx!.lineTo(m.x, m.y);
          ctx!.strokeStyle = grad;
          ctx!.lineWidth = m.size * 0.9;
          ctx!.stroke();

          // head glow
          ctx!.beginPath();
          ctx!.arc(m.x, m.y, m.size * 1.8, 0, Math.PI * 2);
          ctx!.fillStyle = `rgba(220,240,255,${m.opacity * 0.35})`;
          ctx!.fill();
          ctx!.beginPath();
          ctx!.arc(m.x, m.y, m.size * 0.9, 0, Math.PI * 2);
          ctx!.fillStyle = `rgba(255,255,255,${m.opacity * 0.9})`;
          ctx!.fill();
        }
      }

      animId = requestAnimationFrame(draw);
    }

    function startDraw() {
      if (isDrawing) return;
      isDrawing = true;
      animId = requestAnimationFrame(draw);
    }

    function onMouseMove(e: MouseEvent) {
      const rect = canvas!.getBoundingClientRect();
      cursorX = e.clientX - rect.left;
      cursorY = e.clientY - rect.top;
    }
    function onMouseLeave() { cursorX = -9999; cursorY = -9999; }

    function onVisibilityChange() {
      if (document.hidden) {
        isPageVisible = false; isDrawing = false;
        cancelAnimationFrame(animId);
        ctx!.clearRect(0, 0, W, H);
      } else {
        isPageVisible = true;
        initParticles();
        startDraw();
      }
    }

    nextMeteorIn = 120 + Math.floor(Math.random() * 180); // first meteor in 2–5s
    const ro = new ResizeObserver(() => { resize(); initParticles(); meteors = []; });
    ro.observe(canvas);
    resize(); initParticles(); startDraw();

    canvas.addEventListener('mousemove',  onMouseMove);
    canvas.addEventListener('mouseleave', onMouseLeave);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelAnimationFrame(animId);
      ro.disconnect();
      canvas.removeEventListener('mousemove',  onMouseMove);
      canvas.removeEventListener('mouseleave', onMouseLeave);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [density, speed, accent, size]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
}
